import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { IconRefresh, IconTrash } from '@tabler/icons-react';

import { BulkUploadType } from '../../constants/mediaConfig';
import { STATUS_OPTIONS } from '../../constants/status';
import { useContentCategoryOptions } from '../../hooks/useContentCategoryOptions';
import { contentTypes } from '../../types';
import { formatFileSize } from '../../utils/format';
import { MediaThumbnail } from '../MediaThumbnail';

import { FileDraft } from './types';

import { COLORS } from '@/utils/colors';

type Props = {
  pageId: string;
  mediaType: BulkUploadType;
  row: FileDraft;
  isBatchUploading?: boolean;
  onChange: (patch: Partial<FileDraft>) => void;
  onRemove: () => void;
  onRetry?: () => void;
};

export const FileRow = ({
  pageId,
  mediaType,
  row,
  isBatchUploading,
  onChange,
  onRemove,
  onRetry,
}: Props) => {
  const categoryOptions = useContentCategoryOptions(pageId);
  const validationError = row.validation.ok ? null : row.validation;
  const isRowUploading = row.uploadState === 'uploading';
  const isLocked = Boolean(isBatchUploading) || isRowUploading;
  const hasUploadError = row.uploadState === 'error';

  return (
    <Box
      py="sm"
      style={{
        borderBottom: '1px solid var(--mantine-color-dark-5)',
        opacity: isRowUploading ? 0.75 : 1,
      }}
    >
      <Group wrap="nowrap" align="flex-start" gap="sm">
        <Checkbox
          mt={10}
          checked={row.selected}
          disabled={isLocked}
          onChange={(event) => onChange({ selected: event.currentTarget.checked })}
        />

        <MediaThumbnail
          mediaType={mediaType}
          previewUrl={row.previewUrl}
          title={row.name || row.file.name}
        />

        <Stack gap={2} style={{ minWidth: 140, flex: '0 0 160px' }}>
          <Text size="sm" fw={500} lineClamp={1}>
            {row.file.name}
          </Text>
          {validationError && (
            <Text size="xs" c="red">
              {validationError.message}
            </Text>
          )}
          {hasUploadError && (
            <Text size="xs" c="red">
              {row.uploadError || 'Ошибка загрузки'}
            </Text>
          )}
        </Stack>

        <TextInput
          placeholder="Название"
          value={row.name}
          disabled={isLocked}
          onChange={(event) => onChange({ name: event.currentTarget.value })}
          style={{ flex: '1 1 140px', minWidth: 120 }}
          styles={{
            input: { borderColor: COLORS.primary },
          }}
        />

        <Select
          placeholder="Категория"
          data={categoryOptions}
          value={row.category || null}
          disabled={isLocked}
          onChange={(value) => onChange({ category: value ?? '' })}
          searchable
          clearable
          comboboxProps={{ withinPortal: true }}
          style={{ flex: '0 0 160px' }}
          styles={{
            input: { borderColor: COLORS.primary },
          }}
        />

        <Select
          data={STATUS_OPTIONS}
          value={row.status}
          disabled={isLocked}
          onChange={(value) => value && onChange({ status: value as contentTypes.Status })}
          style={{ flex: '0 0 120px' }}
          styles={{
            input: { borderColor: COLORS.primary },
          }}
        />

        <Text size="sm" style={{ flex: '0 0 72px' }}>
          {formatFileSize(row.file.size)}
        </Text>

        {isRowUploading ? (
          <Badge color="blue" variant="light" radius="sm" style={{ flex: '0 0 72px' }}>
            …
          </Badge>
        ) : hasUploadError ? (
          <Badge color="red" variant="filled" radius="sm" style={{ flex: '0 0 72px' }}>
            Ошибка
          </Badge>
        ) : row.validation.ok ? (
          <Badge color="teal" variant="light" radius="sm" style={{ flex: '0 0 56px' }}>
            OK
          </Badge>
        ) : (
          <Badge color="red" variant="filled" radius="sm" style={{ flex: '0 0 72px' }}>
            {row.validation.badge}
          </Badge>
        )}

        <Group gap={4} mt={4} wrap="nowrap">
          {hasUploadError && onRetry ? (
            <Tooltip label="Повторить" withArrow>
              <Button
                variant="subtle"
                size="compact-xs"
                leftSection={<IconRefresh size={14} />}
                onClick={onRetry}
                disabled={isBatchUploading}
              >
                Retry
              </Button>
            </Tooltip>
          ) : null}
          <ActionIcon variant="subtle" color="gray" onClick={onRemove} disabled={isLocked}>
            <IconTrash size={18} />
          </ActionIcon>
        </Group>
      </Group>
    </Box>
  );
};
